<template>
  <!-- Le chemin **nominal** de S27 : « un superviseur déjà connecté atteste depuis son tableau de
       bord, sans jeton ». Le lien signé reçu par courriel reste ouvert, comme rattrapage. -->
  <section class="attestation-queue" aria-label="Preuves à attester">
    <div class="stats-card">
      <span class="card-header">
        <span class="card-title">✅ Preuves à attester</span>
        <span v-if="!isLoading && !hasError" class="badge badge-amber">{{ countLabel }}</span>
      </span>

      <p v-if="isLoading" class="stats-state" role="status">Chargement des preuves…</p>

      <p v-else-if="hasError" class="stats-state" role="alert">
        La file des preuves n'a pas pu être chargée. Réessayez dans un instant.
      </p>

      <!-- État vide explicite : rien n'attend, ce n'est pas un zéro à interpréter. -->
      <p v-else-if="!lines.length" class="stats-state stats-empty">Aucune preuve n'attend d'attestation.</p>

      <ul v-else class="queue-list">
        <li v-for="line in lines" :key="line.id" class="queue-row">
          <div class="queue-body">
            <!-- Prénom seul : le superviseur a l'élève devant lui, la file n'est pas un annuaire. -->
            <p class="queue-learner">{{ line.learnerFirstName }}</p>
            <p class="queue-activity">{{ line.activityTitle }} — {{ line.stepTitle }}</p>
            <p class="queue-evidence">
              <span class="badge badge-grey">{{ line.kindLabel }}</span>
              <span v-if="proofOf(line)" class="queue-proof">{{ proofOf(line) }}</span>
            </p>
          </div>
          <button
            type="button"
            class="queue-button"
            :disabled="pendingId === line.id"
            :aria-label="`Attester la preuve de ${line.learnerFirstName}`"
            @click="attest(line)"
          >
            {{ pendingId === line.id ? "…" : "Attester" }}
          </button>
        </li>
      </ul>

      <p v-if="actionError" class="stats-state" role="alert">{{ actionError }}</p>
    </div>
  </section>
</template>

<script>
import Constants from "@/constants"

/**
 * File d'attestation du superviseur.
 *
 * Une étape qui exige une preuve n'est plus franchie d'un clic : l'apprenant dépose, la tentative
 * reste « en attente de correction », et c'est ce panneau qui la fait aboutir. Attester crédite les
 * points et délivre le certificat — la suite qui manquait au chemin du courriel.
 *
 * RGPD : la ligne ne porte que le prénom, jamais le nom ni le courriel.
 */
export default {
  name: "AttestationQueuePanel",

  props: {
    // Le dashboard hôte est contextuel à un établissement ; la file, elle, couvre toutes les
    // cantines supervisées. On restreint à celle qui est affichée pour ne pas mêler les deux.
    canteenId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      evidences: [],
      status: Constants.LoadingStatus.IDLE,
      pendingId: null,
      actionError: null,
    }
  },

  computed: {
    isLoading() {
      return this.status === Constants.LoadingStatus.LOADING
    },
    hasError() {
      return this.status === Constants.LoadingStatus.ERROR
    },
    lines() {
      if (!this.canteenId) return this.evidences
      return this.evidences.filter((e) => String(e.canteenId) === String(this.canteenId))
    },
    countLabel() {
      const total = this.lines.length
      return `${total} preuve${total > 1 ? "s" : ""}`
    },
  },

  watch: {
    canteenId: "load",
  },

  mounted() {
    this.load()
  },

  methods: {
    proofOf(line) {
      if (line.text) return line.text
      if (line.measuredValue === null || line.measuredValue === undefined) return ""
      return `${line.measuredValue} ${line.measuredUnit || ""}`.trim()
    },
    load() {
      this.status = Constants.LoadingStatus.LOADING
      // `Promise.resolve` protège d'un store dont le mock de test ne déclarerait pas l'action.
      return Promise.resolve(this.$store.dispatch("fetchPendingEvidences"))
        .then((response) => {
          this.evidences = response || []
          this.status = Constants.LoadingStatus.SUCCESS
        })
        .catch(() => {
          this.status = Constants.LoadingStatus.ERROR
        })
    },
    attest(line) {
      if (this.pendingId) return
      this.pendingId = line.id
      this.actionError = null
      return Promise.resolve(this.$store.dispatch("attestEvidence", { evidenceId: line.id }))
        .then(() => {
          // La preuve a quitté la file côté serveur : la retirer ici évite un second envoi
          // qui répondrait « introuvable ».
          this.evidences = this.evidences.filter((e) => e.id !== line.id)
          this.$emit("attested", line.id)
        })
        .catch(() => {
          this.actionError = "L'attestation n'a pas abouti. Réessayez dans un instant."
        })
        .finally(() => {
          this.pendingId = null
        })
    },
  },
}
</script>

<style scoped>
/* Le panneau occupe une cellule de la `.cards-grid` : sa card en prend toute la hauteur
   pour s'aligner sur ses voisines. */
.attestation-queue {
  height: 100%;
}

/* Valeurs reprises de `.card` des dashboards hôtes (styles scoped, non héritables ici). */
.stats-card {
  display: block;
  width: 100%;
  height: 100%;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  width: 100%;
  margin-bottom: 0.75rem;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.stats-state {
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
}

.stats-empty {
  font-style: italic;
}

.queue-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.queue-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.6rem 0;
  border-bottom: 1px solid #f3f4f6;
}

.queue-row:last-child {
  border-bottom: none;
}

.queue-body {
  min-width: 0;
}

.queue-learner {
  font-weight: 700;
  color: #1a1a1a;
  margin: 0;
}

.queue-activity {
  font-size: 0.85rem;
  color: #4b5563;
  margin: 0.1rem 0;
}

.queue-evidence {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin: 0;
}

.queue-proof {
  font-size: 0.85rem;
  color: #6b7280;
}

.queue-button {
  flex: 0 0 auto;
  background: #1e40af;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.4rem 0.9rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
}

.queue-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-amber {
  background: #fef3c7;
  color: #92400e;
}

.badge-grey {
  background: #f3f4f6;
  color: #4b5563;
}
</style>
